import { jsx as _jsx } from "react/jsx-runtime";
import { getFilmsByIds, getMemberWatchlistFilmIds, isEmptyArray, loadAllFilmAvailabilities, LoadingState, loadMemberWatchlistFilms, sortArrayByProperty, SortOrder } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useLoadAction } from '@vista-digital/react-tools';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react';
import { useCallback } from 'react';
import { useOccConfig } from '../../context/occ/hooks/use-occ-config';
import { useMemberRewards } from '../../hooks/use-member-rewards';
import { Button } from '../../subcomponents/button/button';
import { ButtonColor } from '../../subcomponents/button/types/color';
import { ButtonSize } from '../../subcomponents/button/types/size';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { MemberFilmWatchlistFilm as MemberFilmWatchlistFilmComponent } from './subcomponents/film';
import { MemberFilmWatchlistMessage, MemberFilmWatchlistMessageType } from './subcomponents/message';
import { MemberFilmWatchlistSkeleton } from './subcomponents/skeleton';
import { defaultMemberFilmWatchlistProps, memberFilmWatchlistPropsValidator } from './types/props';
const b = bemClassBuilder('v-member-film-watchlist');
export const MemberFilmWatchlist = observer((props) => {
    var _a;
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { assets, config, displayText, events } = props;
    const loadAction = useCallback(() => Promise.all([loadMemberWatchlistFilms(), loadAllFilmAvailabilities()]), []);
    const { loadingState } = useLoadAction(loadAction);
    // Load member rewards to show reward-restricted booking periods.
    useMemberRewards();
    if (loadingState === LoadingState.Failure) {
        return (_jsx(MemberFilmWatchlistMessage, { messageType: MemberFilmWatchlistMessageType.FailedToLoadData, displayText: displayText, assets: assets }));
    }
    if (loadingState === LoadingState.Loading) {
        return enableSkeletonLoadingIndicators ? (_jsx(MemberFilmWatchlistSkeleton, { filmMediaType: config.filmMediaType })) : null;
    }
    const watchlistFilmIds = (_a = getMemberWatchlistFilmIds()) !== null && _a !== void 0 ? _a : [];
    if (isEmptyArray(watchlistFilmIds)) {
        const { onEmptyListButtonClicked } = events;
        return (_jsx(MemberFilmWatchlistMessage, Object.assign({ messageType: MemberFilmWatchlistMessageType.EmptyMemberFilmWatchlist, displayText: displayText, assets: assets }, { children: config.emptyListButton.show && (_jsx(Button, { className: b.element('action-button'), label: displayText.emptyWatchlistActionButtonLabel, color: ButtonColor.Primary, size: ButtonSize.Large, onClick: onEmptyListButtonClicked })) })));
    }
    const films = getFilmsByIds(watchlistFilmIds);
    const sortedFilms = sortArrayByProperty(films, 'releaseDate', SortOrder.Descending);
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsx("ul", Object.assign({ className: b.element('grid') }, { children: _jsx(AnimatePresence, { children: sortedFilms.map(film => (_jsx(MemberFilmWatchlistFilmComponent, Object.assign({ film: film }, props), film.id))) }) })) })));
});
MemberFilmWatchlist.displayName = 'MemberFilmWatchlist';
/**
 * The **Member Film Watchlist** component renders a list of films a Loyalty member has bookmarked.
 */
export const MemberFilmWatchlistComponent = withComponentWrapper(MemberFilmWatchlist, defaultMemberFilmWatchlistProps, memberFilmWatchlistPropsValidator);
